import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { api } from '../services/api';

interface Suggestion {
  type: string;
  category?: string;
  description: string;
  potentialSavingsEur?: number;
}

interface AnalysisResult {
  suggestions: Suggestion[];
  summary: string;
  generatedAt: string;
}

interface MonthContext {
  month: string;
  context: string;
  updatedAt: string;
}

// Finnish translations for suggestion types
const typeLabels: Record<string, string> = {
  cut_spending: 'Vähennä kuluja',
  anomaly: 'Poikkeama',
  recurring_optimization: 'Toistuvat kulut',
  general: 'Yleinen',
  month_guidance: 'Kuukauden ohje',
};

export default function AIAnalysisPage() {
  const [range, setRange] = useState<'month' | '3months' | 'year'>('month');
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().substring(0, 7));
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Generate month options (last 12 months)
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const formatEur = (amount: number) =>
    new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatMonth = (month: string) => {
    const [year, m] = month.split('-');
    const date = new Date(parseInt(year), parseInt(m) - 1, 1);
    return date.toLocaleDateString('fi-FI', { month: 'long', year: 'numeric' });
  };

  // Load context when month changes
  useEffect(() => {
    const loadContext = async () => {
      if (range !== 'month') {
        setContext('');
        return;
      }
      setLoadingContext(true);
      try {
        const data = await api.get<MonthContext>(`/api/ai/context/${selectedMonth}`);
        setContext(data.context || '');
      } catch {
        // No context saved for this month
        setContext('');
      } finally {
        setLoadingContext(false);
      }
    };
    loadContext();
  }, [selectedMonth, range]);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const month = range === 'month' ? selectedMonth : selectedMonth;
      const data = await api.post<AnalysisResult>('/api/ai/analyze', {
        range,
        month,
        context: context.trim() || undefined,
      });
      setResult(data);
      
      // Save context for this month if provided
      if (range === 'month' && context.trim()) {
        try {
          await api.put(`/api/ai/context/${selectedMonth}`, { context: context.trim() });
        } catch {
          // Ignore save errors
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const typeColor = (type: string) => {
    switch (type) {
      case 'cut_spending': return 'error';
      case 'anomaly': return 'warning';
      case 'recurring_optimization': return 'info';
      case 'month_guidance': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Tekoälyanalyysi
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Aikaväli</InputLabel>
              <Select
                value={range}
                label="Aikaväli"
                onChange={(e) => setRange(e.target.value as any)}
              >
                <MenuItem value="month">Kuukausi</MenuItem>
                <MenuItem value="3months">Viimeiset 3 kk</MenuItem>
                <MenuItem value="year">Koko vuosi</MenuItem>
              </Select>
            </FormControl>
            {range === 'month' && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Kuukausi</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Kuukausi"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {monthOptions.map((m) => (
                    <MenuItem key={m} value={m}>{formatMonth(m)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              onClick={handleAnalyze}
              disabled={loading || loadingContext}
              sx={{ alignSelf: 'center' }}
            >
              Analysoi
            </Button>
          </Box>
          
          {range === 'month' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Kuukauden konteksti (valinnainen)"
              placeholder="Esim. 'Tässä kuussa tehtiin keittiöremontti' tai 'Maksettiin ylimääräinen asuntolainan lyhennys' tai 'Lomamatkalla Espanjassa'..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              sx={{ mt: 2 }}
              helperText="Kerro erityisistä tapahtumista, jotta tekoäly voi ottaa ne huomioon analyysissä. Tallennetaan automaattisesti."
              inputProps={{ maxLength: 1000 }}
              disabled={loadingContext}
            />
          )}
          
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Tekoäly analysoi kulutustottumuksesi ja ehdottaa optimointeja käyttäen GPT-4o-mallia.
          </Typography>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {result.summary}
          </Alert>

          {result.suggestions.map((s, idx) => (
            <Card key={idx} sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip 
                    label={typeLabels[s.type] || s.type} 
                    size="small" 
                    color={typeColor(s.type) as any} 
                  />
                  {s.category && (
                    <Typography variant="body2" color="text.secondary">
                      {s.category}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body1">{s.description}</Typography>
                {s.potentialSavingsEur && (
                  <Typography variant="body2" color="success.main" fontWeight={600} sx={{ mt: 0.5 }}>
                    Mahdollinen säästö: {formatEur(s.potentialSavingsEur)} /kk
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </Box>
  );
}
